import { useState, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@app/store/authStore';
import toast from 'react-hot-toast';

const ROLES = [
  { value: 'patient', label: 'Patient', desc: 'Book doctors, track health, manage NHIS' },
  { value: 'doctor', label: 'Doctor', desc: 'See patients, AI charting, billing' },
  { value: 'nurse', label: 'Nurse', desc: 'Home services, patient care' },
  { value: 'physician_assistant', label: 'Physician Assistant', desc: 'Clinical support & consultations' },
  { value: 'pharmacist', label: 'Pharmacist', desc: 'Dispensing, inventory, delivery' },
  { value: 'lab_tech', label: 'Lab Technician', desc: 'Test processing & results' },
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, isLoading } = useAuthStore();

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
    // Only honour a ?role= param if it's a real, self-registerable role. The
    // marketing "For Facilities" link uses ?role=facility, which the API rejects
    // (facility admins are onboarded separately) — fall back to patient so the
    // form never submits an invalid role.
    role: ROLES.some((r) => r.value === searchParams.get('role')) ? searchParams.get('role')! : 'patient',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const update = (field: string, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const validateStep1 = () => {
    const errs: Record<string, string> = {};
    if (!form.role) errs.role = 'Select a role';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep2 = () => {
    const errs: Record<string, string> = {};
    if (!form.first_name) errs.first_name = 'Required';
    if (!form.last_name) errs.last_name = 'Required';
    if (!form.email) errs.email = 'Required';
    if (!form.password) errs.password = 'Required';
    if (form.password.length < 8) errs.password = 'Min 8 characters';
    if (form.password !== form.password_confirmation) errs.password_confirmation = 'Passwords don\'t match';
    if (!acceptedTerms) errs.terms = 'Please accept the Terms and Privacy Policy to continue.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateStep2()) return;

    try {
      await register(form);
      toast.success('Account created! Please verify your email.');
      const paths: Record<string, string> = {
        patient: '/patient/dashboard',
        doctor: '/clinician/dashboard',
        nurse: '/clinician/dashboard',
        physician_assistant: '/clinician/dashboard',
        pharmacist: '/pharmacy/dashboard',
        lab_tech: '/laboratory/dashboard',
      };
      navigate(paths[form.role] || '/patient/dashboard');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } };
      if (error.response?.data?.errors) {
        const fieldErrors: Record<string, string> = {};
        for (const [key, msgs] of Object.entries(error.response.data.errors)) {
          fieldErrors[key] = msgs[0];
        }
        setErrors(fieldErrors);
      } else {
        toast.error(error.response?.data?.message || 'Registration failed');
      }
    }
  };

  return (
    <div className="auth-form-wrapper">
      <h2 className="auth-form-title">Create your account</h2>
      <p className="auth-form-subtitle">
        {step === 1 ? 'How will you use Heka.care?' : 'Complete your profile'}
      </p>

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {[1, 2].map((s) => (
          <div
            key={s}
            style={{
              flex: 1, height: 4, borderRadius: 2,
              background: s <= step ? 'var(--heka-primary-500)' : 'var(--heka-gray-200)',
              transition: 'background 0.2s',
            }}
          />
        ))}
      </div>

      {step === 1 && (
        <div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {ROLES.map((r) => (
              <label
                key={r.value}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '14px 16px', borderRadius: 'var(--radius-md)',
                  border: `1.5px solid ${form.role === r.value ? 'var(--heka-primary-500)' : 'var(--heka-gray-200)'}`,
                  background: form.role === r.value ? 'var(--heka-primary-50)' : 'white',
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
              >
                <input
                  type="radio"
                  name="role"
                  value={r.value}
                  checked={form.role === r.value}
                  onChange={(e) => update('role', e.target.value)}
                  style={{ accentColor: 'var(--heka-primary-500)' }}
                />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--heka-gray-900)' }}>{r.label}</div>
                  <div style={{ fontSize: 12, color: 'var(--heka-gray-500)' }}>{r.desc}</div>
                </div>
              </label>
            ))}
          </div>
          {errors.role && <span className="form-error" style={{ marginTop: 8 }}>{errors.role}</span>}
          <button
            className="auth-btn auth-btn-primary"
            style={{ width: '100%', marginTop: 20 }}
            onClick={() => { if (validateStep1()) setStep(2); }}
          >
            Continue
          </button>
        </div>
      )}

      {step === 2 && (
        <form className="auth-form" onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">First name</label>
              <input className={`form-input ${errors.first_name ? 'error' : ''}`} value={form.first_name} onChange={(e) => update('first_name', e.target.value)} />
              {errors.first_name && <span className="form-error">{errors.first_name}</span>}
            </div>
            <div className="form-group">
              <label className="form-label">Last name</label>
              <input className={`form-input ${errors.last_name ? 'error' : ''}`} value={form.last_name} onChange={(e) => update('last_name', e.target.value)} />
              {errors.last_name && <span className="form-error">{errors.last_name}</span>}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Email address</label>
            <input type="email" className={`form-input ${errors.email ? 'error' : ''}`} value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="you@example.com" />
            {errors.email && <span className="form-error">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Phone number (optional)</label>
            <input type="tel" className="form-input" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="+233 XX XXX XXXX" />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="reg-password">Password</label>
            <div className="password-field">
              <input
                id="reg-password"
                type={showPassword ? 'text' : 'password'}
                className={`form-input ${errors.password ? 'error' : ''}`}
                value={form.password}
                onChange={(e) => update('password', e.target.value)}
                placeholder="Min 8 characters"
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-field-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '\uD83D\uDE48' : '\uD83D\uDC41\uFE0F'}
              </button>
            </div>
            {errors.password && <span className="form-error">{errors.password}</span>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="reg-confirm">Confirm password</label>
            <div className="password-field">
              <input
                id="reg-confirm"
                type={showConfirm ? 'text' : 'password'}
                className={`form-input ${errors.password_confirmation ? 'error' : ''}`}
                value={form.password_confirmation}
                onChange={(e) => update('password_confirmation', e.target.value)}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-field-toggle"
                onClick={() => setShowConfirm((v) => !v)}
                aria-label={showConfirm ? 'Hide password' : 'Show password'}
              >
                {showConfirm ? '\uD83D\uDE48' : '\uD83D\uDC41\uFE0F'}
              </button>
            </div>
            {errors.password_confirmation && <span className="form-error">{errors.password_confirmation}</span>}
          </div>

          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 4, fontSize: 13, color: 'var(--heka-gray-700)' }}>
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              style={{ marginTop: 3, accentColor: 'var(--heka-primary-500)' }}
            />
            <span>
              I agree to Heka.care's{' '}
              <Link to="/legal/terms" target="_blank" style={{ color: 'var(--heka-primary-600)', fontWeight: 600 }}>Terms of Service</Link>
              {' '}and{' '}
              <Link to="/legal/privacy" target="_blank" style={{ color: 'var(--heka-primary-600)', fontWeight: 600 }}>Privacy Policy</Link>.
            </span>
          </label>
          {errors.terms && <span className="form-error" style={{ marginTop: 4 }}>{errors.terms}</span>}

          <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
            <button type="button" className="auth-btn" style={{ flex: 1, background: 'var(--heka-gray-100)', color: 'var(--heka-gray-700)' }} onClick={() => setStep(1)}>
              Back
            </button>
            <button type="submit" className="auth-btn auth-btn-primary" style={{ flex: 2 }} disabled={isLoading}>
              {isLoading ? 'Creating account…' : 'Create account'}
            </button>
          </div>
        </form>
      )}

      <p className="auth-footer-text">
        Already have an account? <Link to="/login">Sign in</Link>
      </p>

      <p className="auth-legal">
        By creating an account you agree to our{' '}
        <Link to="/legal/terms">Terms of Service</Link> and{' '}
        <Link to="/legal/privacy">Privacy Policy</Link>.
        Read our <Link to="/legal/nhis-compliance">NHIS compliance statement</Link>.
      </p>
    </div>
  );
}
